<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <button type="button" @click="$router.go(-1)" class="btn-back">
            <font-awesome-icon icon="arrow-left" />
          </button>
          <h5 class="page-title mt-2">New Expense</h5>
          <p class="page-sub">Create and submit a new expense record</p>
        </div>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <!-- Loading -->
      <div v-if="loading" class="form-card mb-4">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 4" :key="n"></div>
        </div>
        <div class="skeleton skeleton--field mt-3" style="width:60%"></div>
        <div class="skeleton skeleton--area mt-3"></div>
      </div>
  
      <form v-else @submit.prevent="createData">
  
        <!-- Expense Details -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--orange">
              <font-awesome-icon icon="receipt" />
            </div>
            <div>
              <p class="section-title">Expense Details</p>
              <p class="section-sub">Fill in the expense information below</p>
            </div>
          </div>
  
          <div class="fields-grid">
  
            <div class="field-group">
              <label class="field-label">Expense Date <span class="req">*</span></label>
              <div class="datepicker-wrap">
                <font-awesome-icon icon="calendar" class="datepicker-icon" />
                <Datepicker
                  v-model="expense.transaction_date"
                  :enableTimePicker="true"
                  class="clean-datepicker"
                />
              </div>
            </div>
  
            <div class="field-group">
              <label class="field-label">Expense Category <span class="req">*</span></label>
              <Multiselect
                v-model="expense.ledger_id"
                label="name"
                :options="category"
                placeholder="Select category"
                :searchable="true"
                :create-option="false"
                :close-on-select="true"
                class="custom-multiselect"
              />
            </div>
  
            <div class="field-group">
              <label class="field-label">Currency <span class="req">*</span></label>
              <Multiselect
                v-model="expense.currency_id"
                label="name"
                :options="currency_arr"
                placeholder="Select currency"
                :searchable="true"
                :create-option="false"
                :close-on-select="true"
                class="custom-multiselect"
              />
            </div>
  
            <div class="field-group">
              <label class="field-label">Payment Type <span class="req">*</span></label>
              <Multiselect
                v-model="expense.payment_type_id"
                label="name"
                :options="paymentTypes"
                placeholder="Select payment type"
                :searchable="true"
                :create-option="false"
                :close-on-select="true"
                class="custom-multiselect"
                @change="fetchPayment($event)"
              />
            </div>
  
            <!-- Bank — only shown for card payments -->
            <div class="field-group" v-if="paymentType?.payment_type === 'card'">
              <label class="field-label">Bank</label>
              <Multiselect
                v-model="expense.bank_id"
                label="name"
                :options="bank_arr"
                placeholder="Select bank"
                :searchable="true"
                :create-option="false"
                :close-on-select="true"
                class="custom-multiselect"
              />
            </div>
  
            <div class="field-group">
              <label class="field-label">Expense Amount <span class="req">*</span></label>
              <input
                type="text"
                class="field-input"
                v-model="expense.amount"
                placeholder="0.00"
                onkeypress="if(isNaN(this.value+String.fromCharCode(event.keyCode)))return false;"
              />
            </div>
  
            <div class="field-group field-group--full">
              <label class="field-label">Title <span class="req">*</span></label>
              <input
                type="text"
                class="field-input"
                v-model="expense.title"
                placeholder="Short expense description"
              />
            </div>
  
            <div class="field-group field-group--full">
              <label class="field-label">Description</label>
              <textarea
                class="field-input"
                v-model="expense.description"
                rows="4"
                placeholder="Full expense description (optional)"
              ></textarea>
            </div>
  
          </div>
        </div>
  
        <!-- Footer -->
        <div class="form-footer">
          <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
          <button type="submit" class="btn-primary-action" :disabled="crudLoading">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <font-awesome-icon v-else icon="plus" class="me-2" />
            {{ crudLoading ? 'Creating...' : 'Create Expense' }}
          </button>
        </div>
  
      </form>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import Multiselect from '@vueform/multiselect';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import Header from './component/header.vue';
  
  export default {
    components: { Multiselect, Header },
  
    data() {
      return {
        toast:            useToast(),
        store:            useStore(),
        validationErrors: [],
        currency_arr:     [],
        bank_arr:         [],
        category:         [],
        paymentTypes:     [],
        paymentType:      null,
        crudLoading:      false,
        loading:          false,
  
        expense: {
          transaction_date: new Date(),
          payment_type_id:  '',
          currency_id:      '',
          bank_id:          '',
          ledger_id:        '',
          title:            '',
          amount:           0.00,
          description:      '',
        },
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() {
      // Load all dropdowns in parallel
      Promise.all([
        this.fetchCategory(),
        this.fetchCurrency(),
        this.fetchBanks(),
        this.fetchPaymentTypes(),
      ]);
    },
  
    methods: {
      async fetchCurrency() {
        axios.get('/settings/currency').then(response => {
          this.currency_arr = (response?.data?.data?.data ?? []).map(c => ({
            value: c.id, name: `${c.name} - ${c.symbol}`,
          }));
        }).catch(() => {});
      },
  
      async fetchBanks() {
        axios.get('/settings/bank/all/active').then(response => {
          this.bank_arr = (response?.data?.data ?? []).map(b => ({
            value: b.id, name: b.bank_name,
          }));
        }).catch(() => {});
      },
  
      async fetchCategory() {
        axios.get('/accounting/expense/category/active').then(response => {
          this.category = (response?.data?.data ?? []).map(c => ({
            value: c.id, name: c.name,
          }));
        }).catch(() => {});
      },
  
      async fetchPaymentTypes() {
        this.loading = true;
        axios.get('/settings/payment_type/fetch/all').then(response => {
          this.paymentTypes = (response?.data?.data ?? []).map(p => ({
            value: p.id, name: p.name,
          }));
          this.loading = false;
        }).catch(() => { this.loading = false; });
      },
  
      async fetchPayment(id) {
        if (!id) return;
        axios.get(`/settings/payment_type/${id}`).then(response => {
          this.paymentType = response.data.data;
          if (this.paymentType?.payment_type === 'cash') {
            this.expense.bank_id = '';
          }
        }).catch(() => {});
      },
  
      async createData() {
        this.crudLoading     = true;
        this.validationErrors = [];
        axios.post('accounting/expense/create', this.expense)
          .then(response => {
            this.crudLoading = false;
            if (response.data.status === 200) {
              this.toast.success(response.data.message);
              this.$router.push({ path: '/manage-expenses' });
            } else {
              this.toast.error(response.data.message);
            }
          })
          .catch(error => {
            this.crudLoading = false;
            const msg = error.response?.data?.message;
            if (Array.isArray(msg)) { this.validationErrors = msg; }
            else this.toast.error(msg ?? 'Something went wrong');
          });
      },
    },
  };
  </script>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header { margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .btn-back    { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:4px; transition:color .15s; }
  .btn-back:hover { color:#1a2b45; }
  
  /* ── Error Banner ── */
  .error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:20px; color:#e03535; font-size:13px; }
  .error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }
  
  /* ── Skeleton ── */
  .form-card    { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4         { margin-bottom:16px; }
  .skeleton-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .skeleton     { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  .skeleton--area  { height:110px; }
  .mt-3 { margin-top:12px; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:20px; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--orange { background:rgba(224,140,0,.1); color:#e08c00; }
  .section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Fields ── */
  .fields-grid       { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
  .field-group       { display:flex; flex-direction:column; gap:6px; }
  .field-group--full { grid-column:1/-1; }
  .field-label       { font-size:13px; font-weight:600; color:#1a2b45; }
  .req               { color:#e03535; }
  
  .field-input { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; resize:vertical; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Datepicker ── */
  .datepicker-wrap { position:relative; }
  .datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  :deep(.clean-datepicker.is-active) { border-color:#004AAD !important; box-shadow:0 0 0 3px rgba(0,74,173,.08) !important; }
  
  /* ── Multiselect ── */
  :deep(.custom-multiselect) { --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-font-size:13.5px; --ms-ring-color:rgba(0,74,173,.08); }
  :deep(.custom-multiselect.is-active) { --ms-border-color:#004AAD; }
  
  /* ── Footer ── */
  .form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:4px; }
  .btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:10px 28px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  .me-2 { margin-right:6px; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .fields-grid { grid-template-columns:repeat(2,1fr); } .skeleton-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px) {
    .fields-grid,.skeleton-grid { grid-template-columns:1fr; }
    .form-card { padding:16px; }
    .form-footer { flex-direction:column; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
  }
  </style>